// A wall's along-axis in world XZ. Pure — in shared-lib so the take-off
// (roomTakeOffs) can run on the functions side, which cannot load the
// three.js-backed web-app/src/utils/wallGeometryUtils.js it came from.

/**
 * Get the 2D wall axis in world XZ: unit direction (bottomLeft → bottomRight),
 * length, and midpoint. Prefers `wall.alignment.bottomLeft/bottomRight`;
 * falls back to a rotation-derived axis when those aren't present.
 *
 * IMPORTANT: walls in this codebase store `rotation.y = atan2(-dz, dx)`, so
 * the correct rotation-derived direction is `(cos(ry), -sin(ry))` — NOT
 * `(cos(ry), sin(ry))`. The latter flips the sign of the Z component and
 * collapses diagonal walls onto a wrong axis (zero projection at 45°).
 * Use this helper anywhere you need a wall's along-direction for projection,
 * span, or endpoint math.
 *
 * @param {Object} wall - Wall with `alignment` and/or `rotation` + `center`.
 * @returns {{dirX:number, dirZ:number, len:number, midX:number, midZ:number}}
 */
const getWallAxisXZ = (wall) => {
  const a = wall?.alignment?.bottomLeft;
  const b = wall?.alignment?.bottomRight;
  if (a && b) {
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const len = Math.hypot(dx, dz);
    if (len > 0) {
      return {
        dirX: dx / len,
        dirZ: dz / len,
        len,
        midX: (a.x + b.x) / 2,
        midZ: (a.z + b.z) / 2,
      };
    }
  }
  const ry = wall?.rotation?.y || 0;
  const len = wall?.dimensions?.x || 0;
  const cx = wall?.center?.x ?? 0;
  const cz = wall?.center?.z ?? 0;
  return {
    dirX: Math.cos(ry),
    dirZ: -Math.sin(ry),
    len,
    midX: cx,
    midZ: cz,
  };
};

module.exports = { getWallAxisXZ };
